import React, {useEffect, useState} from "react";
import {
    Autocomplete,
    Button, Checkbox, Divider, FormControlLabel, Grid, IconButton, LinearProgress, Paper, Tab, Tabs, TextField,
    Typography
} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import {AxiosError} from "axios";
import {Box} from "@mui/system";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../../../redux/store";
import {useSnack} from "../../../utils/useSnack";
import {Company} from "../../../types/company";
import {companyService} from "../../../api/services/company";
import {CLEAR_COMPANY, SET_COMPANY} from "../../../redux/reducers/company";
import digitsOnly from "../../../utils/digitsOnly";
import TabContext from "@mui/lab/TabContext";
import {LoadingButton, TabPanel} from "@mui/lab";
import {Field, FieldProps, Formik, FormikProps} from "formik";
import {initialValues, NewAccreditationForm} from "./formik";
import {FormikHelpers} from "formik/dist/types";
import {ValidationSchema} from "../../account/NewAuction/components/Contest/formik";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ClearIcon from "@mui/icons-material/Clear";
import {useDictionary} from "../../../utils/useDictionary";
import {AccreditationState, CreditRating, PaymentFlexibility, WorkCondition} from "../../../utils/enums";

export default function CompanyDetails() {
    const {snack} = useSnack();
    const {t} = useTranslation();
    const state = useSelector((state: State) => state);
    const dispatch = useDispatch();
    const company: Company | null = state.company.company;

    const navigate = useNavigate();
    const onClose = () => navigate("/moderator-companies");
    const back = () => navigate("/admin-companies")
    const location = useLocation();
    const pathArray = location.pathname.split('/');

    const companyId = +pathArray[pathArray.length - 1];

    const [loading, setLoading] = useState(false);

    async function getCompany() {
        setLoading(true);
        try {
            const response = await companyService.getCompany(companyId);
            if (response.status === "success" && response.data) {
                dispatch({type: SET_COMPANY, payload: response.data})
            } else {
                snack(t("snack.error.company_details"), "error")
            }
        } catch (error) {
            const err = error as AxiosError
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        if (companyId) getCompany();
        return () => {
            dispatch({type: CLEAR_COMPANY})
        }
    }, [companyId]);
    const [tabValue, setTabValue] = useState("profile");

    const handleTabChange = (event: React.SyntheticEvent, newTabValue: string) => {
        setTabValue(newTabValue);
    };

    const commonFormikRef = React.useRef<FormikProps<NewAccreditationForm>>(null);
    const onSubmit = async (values: NewAccreditationForm, formikHelpers: FormikHelpers<NewAccreditationForm>) => {
    }

    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    const submit = () => {
        console.log('submited')
    }
    const [disableForm, setDisableForm] = useState(false);

    const creditRating = useDictionary(
        CreditRating,
        (type) => ({value: type, label: t(`credit_rating.${type}`)}),
        {value: CreditRating.no, label: t("credit_rating.no")}
    );

    const workCondition = useDictionary(
        WorkCondition,
        (type) => ({value: type, label: t(`work_condition.${type}`)}),
        {value: WorkCondition.no_info, label: t("work_condition.no_info")}
    );

    const paymentFlexibility = useDictionary(
        PaymentFlexibility,
        (type) => ({value: type, label: t(`payment_flexibility.${type}`)}),
        {value: PaymentFlexibility.advance, label: t("payment_flexibility.advance")}
    )

    const accreditationState = useDictionary(
        AccreditationState,
        (type) => ({value: type, label: t(`accreditation_state.${type}`)}),
        {value: AccreditationState.not_checked, label: t("accreditation_state.not_checked")}
    )
    const downloadResultFile = () => {
        console.log('download')
    }
    return (
        <Paper sx={{pl: 4, pr: 4, pt: 2, pb: 4}} elevation={10}>
            <Grid container spacing={3} justifyContent="space-between">
                <Grid item>
                    <Typography variant="h5">
                        {company?.id
                            ? `Профиль компании ${company ? `- ${company.shortTitle}` : ""}`
                            : `Карточка организации ${company ? `- ${company.shortTitle}` : ""}`
                        }
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={1}>
                    <Button fullWidth color="primary" variant="contained" onClick={back}>
                              <span style={{whiteSpace: 'nowrap'}}>
                                  {t("common.back_button")}
                              </span>
                    </Button>
                </Grid>
            </Grid>

            <Divider sx={{mt: 2, mb: 6, width: "100%"}}/>

            <TabContext value={tabValue}>
                <Box sx={{width: "100%", mb: 3, borderBottom: 1, borderColor: 'divider'}}>
                    <Tabs value={tabValue} onChange={handleTabChange}
                          textColor="primary" indicatorColor="primary" variant="scrollable"
                    >
                        <Tab key="profile" value="profile" sx={{maxWidth: "100%"}}
                             label={
                                 <Typography sx={{fontSize: {xs: 14, sm: 16, md: 20}, textTransform: "none"}}>
                                     Профиль
                                 </Typography>
                             }
                        />
                        <Tab key="accreditation" value="accreditation" sx={{maxWidth: "100%"}}
                             label={
                                 <Typography sx={{fontSize: {xs: 14, sm: 16, md: 20}, textTransform: "none"}}>
                                     Аккредитация
                                 </Typography>
                             }
                        />

                    </Tabs>
                    <TabPanel value="profile">
                        {loading
                            ? <Box>
                                <LinearProgress sx={{mt: 3, mb: 1}} color="primary"/>
                                <Typography>
                                    {t("cabinet.company_details.loading")}
                                </Typography>
                            </Box>
                            : !company
                                ? <Box>
                                    <Typography>
                                        {t("cabinet.company_details.error")}
                                    </Typography>
                                </Box>
                                : <Grid container rowSpacing={3} sx={{marginTop: 2, width: "100%"}}>
                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.company_details.owner")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.owner_email")}
                                                value={company.ownerData.username}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.owner_name")}
                                                value={company.ownerData.name}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.owner_phone")}
                                                value={company.ownerData.mobile}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>

                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.company_details.ceo")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.ceo_phone")}
                                                value={company.ceoPhone}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.ceo_name")}
                                                value={company.ceoName}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>

                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.company_details.organization")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.itn")}
                                                value={digitsOnly(company.itn)}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.title")}
                                                value={company.title}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.short_title")}
                                                value={company.shortTitle}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.legal_form")}
                                                value={t(`legal_form.${company.legalForm}`)}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.size")}
                                                value={t(`size.${company.size}`)}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.ownership_form")}
                                                value={t(`ownership.${company.ownershipForm}`)}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>

                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.company_details.contacts")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.legal_address")}
                                                value={company.legalAddress}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.actual_address")}
                                                value={company.actualAddress}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container columnSpacing={3} rowSpacing={1} sx={{mt: 1}}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.phone")}
                                                value={company.phone}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth color="primary" type="text" size="small"
                                                label={t("auction.fields.company.email")}
                                                value={company.email}
                                                InputProps={{readOnly: true}}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                        }

                        {company?.id
                            ? null
                            : <>
                                <Divider sx={{mt: 6, mb: 2, width: "100%"}}/>

                                <Grid item container spacing={2} justifyContent="center" sx={{pt: 2}}>
                                    <Grid item xs="auto">
                                        <Button fullWidth variant="contained" size="large"
                                                onClick={onClose} color="secondary"
                                        >
                                            {t("auction.close")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </>
                        }
                    </TabPanel>

                    <TabPanel value="accreditation">
                        <Formik innerRef={commonFormikRef}
                                initialValues={initialValues}
                                validationSchema={ValidationSchema()}
                                onSubmit={onSubmit}
                                validateOnMount>
                            {(frops: FormikProps<NewAccreditationForm>) => (
                                <Grid container rowSpacing={3} sx={{marginTop: 2, width: "100%"}}>
                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                                <Typography variant="h6">
                                                    {t("cabinet.accreditation_details.financial_stability")}
                                                </Typography>
                                                <Grid xs={4}>
                                                    <Autocomplete
                                                        size="small"
                                                        options={accreditationState.dictionary}
                                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                                        value={accreditationState.selected}
                                                        onChange={accreditationState.onSelectChange}
                                                        renderInput={params =>
                                                            <Field name={"accreditationState"}>
                                                                {({field, form, meta}: FieldProps) => (
                                                                    <TextField {...params} fullWidth color="primary"
                                                                               error={!!(meta.touched && meta.error)}
                                                                               helperText={meta.touched && meta.error}
                                                                    />
                                                                )}
                                                            </Field>
                                                        }
                                                    />
                                                </Grid>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field name={'netProfit'}>
                                                {({field, form, meta}: FieldProps) => (
                                                    <TextField {...field}
                                                               disabled={disableForm}
                                                               fullWidth color="primary" size="small"
                                                               label={t("cabinet.accreditation.annualRevenue")}
                                                               error={!!(meta.touched && meta.error)}
                                                               helperText={meta.touched && meta.error}
                                                               onBlur={() => {
                                                                   if (+field.value === 0) form.setFieldValue(`annualRevenue`, null);
                                                               }}
                                                               InputProps={{
                                                                   endAdornment: <IconButton
                                                                       onClick={() => form.setFieldValue(`annualRevenue`, null)}
                                                                       size="small"
                                                                       color="primary"
                                                                   >
                                                                       <ClearIcon/>
                                                                   </IconButton>
                                                               }}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field name={'netProfit'}>
                                                {({field, form, meta}: FieldProps) => (
                                                    <TextField {...field}
                                                               disabled={disableForm}
                                                               fullWidth color="primary" size="small"
                                                               label={t("cabinet.accreditation.netProfit")}
                                                               error={!!(meta.touched && meta.error)}
                                                               helperText={meta.touched && meta.error}
                                                               onBlur={() => {
                                                                   if (+field.value === 0) form.setFieldValue(`netProfit`, null);
                                                               }}
                                                               InputProps={{
                                                                   endAdornment: <IconButton
                                                                       onClick={() => form.setFieldValue(`netProfit`, null)}
                                                                       size="small"
                                                                       color="primary"
                                                                   >
                                                                       <ClearIcon/>
                                                                   </IconButton>
                                                               }}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="bankGuarantee"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.bankGuarantee")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Autocomplete
                                                size="small"
                                                options={creditRating.dictionary}
                                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                                value={creditRating.selected}
                                                onChange={creditRating.onSelectChange}
                                                renderInput={params =>
                                                    <Field name={"creditRating"}>
                                                        {({field, form, meta}: FieldProps) => (
                                                            <TextField {...params} fullWidth color="primary"
                                                                       label={t("auction.dictionary.creditRating")}
                                                                       error={!!(meta.touched && meta.error)}
                                                                       helperText={meta.touched && meta.error}
                                                            />
                                                        )}
                                                    </Field>
                                                }
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>

                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.accreditation_details.experience_and_qualifications")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field name={'objectCount'}>
                                                {({field, form, meta}: FieldProps) => (
                                                    <TextField {...field}
                                                               disabled={disableForm}
                                                               fullWidth color="primary" size="small"
                                                               label={t("cabinet.accreditation.objectCount")}
                                                               error={!!(meta.touched && meta.error)}
                                                               helperText={meta.touched && meta.error}
                                                               onBlur={() => {
                                                                   if (+field.value === 0) form.setFieldValue(`objectCount`, null);
                                                               }}
                                                               InputProps={{
                                                                   endAdornment: <IconButton
                                                                       onClick={() => form.setFieldValue(`annualRevenue`, null)}
                                                                       size="small"
                                                                       color="primary"
                                                                   >
                                                                       <ClearIcon/>
                                                                   </IconButton>
                                                               }}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field name={'averageCostOfProjects'}>
                                                {({field, form, meta}: FieldProps) => (
                                                    <TextField {...field}
                                                               disabled={disableForm}
                                                               fullWidth color="primary" size="small"
                                                               label={t("cabinet.accreditation.averageCostOfProjects")}
                                                               error={!!(meta.touched && meta.error)}
                                                               helperText={meta.touched && meta.error}
                                                               onBlur={() => {
                                                                   if (+field.value === 0) form.setFieldValue(`averageCostOfProjects`, null);
                                                               }}
                                                               InputProps={{
                                                                   endAdornment: <IconButton
                                                                       onClick={() => form.setFieldValue(`averageCostOfProjects`, null)}
                                                                       size="small"
                                                                       color="primary"
                                                                   >
                                                                       <ClearIcon/>
                                                                   </IconButton>
                                                               }}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field name={'geographyOfProjects'}>
                                                {({field, form, meta}: FieldProps) => (
                                                    <TextField {...field}
                                                               disabled={disableForm}
                                                               fullWidth color="primary" size="small"
                                                               label={t("cabinet.accreditation.geographyOfProjects")}
                                                               error={!!(meta.touched && meta.error)}
                                                               helperText={meta.touched && meta.error}
                                                               onBlur={() => {
                                                                   if (+field.value === 0) form.setFieldValue(`geographyOfProjects`, null);
                                                               }}
                                                               InputProps={{
                                                                   endAdornment: <IconButton
                                                                       onClick={() => form.setFieldValue(`geographyOfProjects`, null)}
                                                                       size="small"
                                                                       color="primary"
                                                                   >
                                                                       <ClearIcon/>
                                                                   </IconButton>
                                                               }}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>

                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.accreditation_details.certification")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="iso9001"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.iso9001")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="iso14001"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.iso14001")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="leed"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.leed")}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>
                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.accreditation_details.esc_and_sustainability")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="environmentalPolicy"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.environmentalPolicy")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="useOfEnvironmentallyFriendlyMaterials"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.useOfEnvironmentallyFriendlyMaterials")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field name={'wasteRecyclingPercentage'}>
                                                {({field, form, meta}: FieldProps) => (
                                                    <TextField {...field}
                                                               disabled={disableForm}
                                                               fullWidth color="primary" size="small"
                                                               label={t("cabinet.accreditation.wasteRecyclingPercentage")}
                                                               error={!!(meta.touched && meta.error)}
                                                               helperText={meta.touched && meta.error}
                                                               onBlur={() => {
                                                                   if (+field.value === 0) form.setFieldValue(`wasteRecyclingPercentage`, null);
                                                               }}
                                                               InputProps={{
                                                                   endAdornment: <IconButton
                                                                       onClick={() => form.setFieldValue(`wasteRecyclingPercentage`, null)}
                                                                       size="small"
                                                                       color="primary"
                                                                   >
                                                                       <ClearIcon/>
                                                                   </IconButton>
                                                               }}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Autocomplete
                                                size="small"
                                                options={workCondition.dictionary}
                                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                                value={workCondition.selected}
                                                onChange={workCondition.onSelectChange}
                                                renderInput={params =>
                                                    <Field name={"workCondition"}>
                                                        {({field, form, meta}: FieldProps) => (
                                                            <TextField {...params} fullWidth color="primary"
                                                                       label={t("auction.dictionary.workCondition")}
                                                                       error={!!(meta.touched && meta.error)}
                                                                       helperText={meta.touched && meta.error}
                                                            />
                                                        )}
                                                    </Field>
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>
                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.accreditation_details.management_project")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="availabilityOfControlSystems"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.availabilityOfControlSystems")}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button onClick={downloadResultFile}>
                                                {t("cabinet.accreditation.availabilityOfControlSystems")}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>
                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.accreditation_details.prices_and_commerce")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field name={'totalPriceAccordingToEstimate'}>
                                                {({field, form, meta}: FieldProps) => (
                                                    <TextField {...field}
                                                               disabled={disableForm}
                                                               fullWidth color="primary" size="small"
                                                               label={t("cabinet.accreditation.totalPriceAccordingToEstimate")}
                                                               error={!!(meta.touched && meta.error)}
                                                               helperText={meta.touched && meta.error}
                                                               onBlur={() => {
                                                                   if (+field.value === 0) form.setFieldValue(`totalPriceAccordingToEstimate`, null);
                                                               }}
                                                               InputProps={{
                                                                   endAdornment: <IconButton
                                                                       onClick={() => form.setFieldValue(`wasteRecyclingPercentage`, null)}
                                                                       size="small"
                                                                       color="primary"
                                                                   >
                                                                       <ClearIcon/>
                                                                   </IconButton>
                                                               }}
                                                    />
                                                )}
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Autocomplete
                                                size="small"
                                                options={paymentFlexibility.dictionary}
                                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                                value={paymentFlexibility.selected}
                                                onChange={paymentFlexibility.onSelectChange}
                                                renderInput={params =>
                                                    <Field name={"paymentFlexibility"}>
                                                        {({field, form, meta}: FieldProps) => (
                                                            <TextField {...params} fullWidth color="primary"
                                                                       label={t("auction.dictionary.paymentFlexibility")}
                                                                       error={!!(meta.touched && meta.error)}
                                                                       helperText={meta.touched && meta.error}
                                                            />
                                                        )}
                                                    </Field>
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>
                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.accreditation_details.legal_purity_and_kyc")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="registrationInStateRegisters"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.registrationInStateRegisters")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="noLitigation"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.noLitigation")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="registerOfUnscrupulousSuppliers"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.registerOfUnscrupulousSuppliers")}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>

                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.accreditation_details.compliance_and_anticorruption")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="availabilityOfCompliancePolicy"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.availabilityOfCompliancePolicy")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field
                                                type="checkbox"
                                                name="anHonestyAgreementHasBeenSigned"
                                                as={FormControlLabel}
                                                control={<Checkbox/>}
                                                label={t("cabinet.accreditation.anHonestyAgreementHasBeenSigned")}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Divider sx={{my: 2, ml: "auto", width: '98%'}}/>

                                    <Grid container columnSpacing={3} rowSpacing={1}>
                                        <Grid item xs={12} sx={{mb: 1}}>
                                            <Typography variant="h6">
                                                {t("cabinet.accreditation_details.documents_and_confirmations")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="subtitle2">{t("cabinet.accreditation_details.certificates_and_licenses")}</Typography>
                                            <Button onClick={downloadResultFile}>
                                                {"license.pdf"}
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="subtitle2">{t("cabinet.accreditation_details.tax_office")}</Typography>
                                            <Button onClick={downloadResultFile}>
                                                {"nalog2025.pdf"}
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="subtitle2">{t("cabinet.accreditation_details.accounting_statements")}</Typography>
                                            <Button onClick={downloadResultFile}>
                                                {"audit_2024.pdf"}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Formik>
                        <Grid item container spacing={2} justifyContent="center" sx={{pt: 2}}>
                            <Grid item xs="auto">
                                <Button fullWidth variant="contained" size="large"
                                        onClick={onClose} color="secondary"
                                >
                                    {t("cabinet.accreditation.return")}
                                </Button>
                            </Grid>
                            <Grid item xs="auto">
                                <Button fullWidth variant="contained" size="large"
                                        onClick={onClose} color="secondary"
                                >
                                    {t("cabinet.accreditation.notAccredited")}
                                </Button>
                            </Grid>
                            <Grid item xs="auto">
                                <Button fullWidth variant="contained" color="primary"
                                        onClick={submit} size="large">
                                    {t("cabinet.accreditation.accredited")}
                                </Button>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </Box>
            </TabContext>
        </Paper>
    )
}
